import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { PageLoading } from '@dynamika-shared/ui/PageLoading/PageLoading';
import { Auth0ContextInterface, useAuth0 } from '@auth0/auth0-react';
import { AuthUser } from '@dynamika-shared/types/auth';
import { ErrorPage } from 'src/pages/Error/Error';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { useLogout } from '@hooks/useLogout';
import { resolveAuthFlow } from '@router/authFlow';

export const WithAuthorizationHoc: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const searchParams = new URLSearchParams(window.location.search);
  const error = searchParams.get('error') ?? '';
  const errorDescription = searchParams.get('error_description') ?? '';
  const auth0: Auth0ContextInterface<AuthUser> = useAuth0();
  const [setUser, user] = useAuthSlice((state, actions) => [
    actions.setUser,
    state.data,
  ]);
  const { logoutLocally } = useLogout();
  const [canRender, setCanRender] = useState(false);

  /**
   * Plain terms: an SSO link must not open the account of whoever is still
   * signed in on this browser — sign them out first, then go to login.
   *
   * Technical: Auth0 restores the old tokens from localStorage, so without the
   * reset `loginWithRedirect` (which carries `organization`) never runs. Auth0
   * returns to origin, not `/sso`, so this fires once per SSO entry.
   */
  const [ssoResetPending, setSsoResetPending] = useState(
    () => resolveAuthFlow(window.location).type === 'sso'
  );

  useEffect(() => {
    if (!ssoResetPending || auth0.isLoading) {
      return;
    }

    if (!auth0.isAuthenticated) {
      setSsoResetPending(false);
      return;
    }

    logoutLocally()
      .catch(err => console.error('Failed to clear session on SSO entry:', err))
      .finally(() => setSsoResetPending(false));
  }, [ssoResetPending, auth0.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const addTokenAndGetMe = useCallback(async () => {
    if (!user) {
      const claims = await auth0.getIdTokenClaims();
      if (claims) {
        sessionStorage.setItem('idToken', claims.__raw);
        sessionStorage.setItem('idTokenExpiration', claims.exp + '');
        if (auth0.user) {
          setUser(auth0.user);
        }
        setCanRender(true);
      }
    }
  }, [auth0, user]);

  if (error || errorDescription) {
    return <ErrorPage error={error || errorDescription} />;
  }

  if (!ssoResetPending && !auth0.isLoading && !auth0.isAuthenticated) {
    auth0.loginWithRedirect({
      appState: {
        returnTo: window.location.pathname + window.location.search,
      },
    });
  }

  if (!ssoResetPending && auth0.isAuthenticated) {
    addTokenAndGetMe().then();
  }

  const shouldRenderLoadingState =
    ssoResetPending ||
    (!auth0.isLoading && !auth0.isAuthenticated) ||
    auth0.isLoading ||
    !canRender;

  return shouldRenderLoadingState ? (
    <div className='relative h-full w-full'>
      <PageLoading className='top-[50%]' />
    </div>
  ) : (
    <>{children}</>
  );
};
